/**
 * The `lab` page type: a diagram plus a button that opens a practical
 * site in its own tab.
 *
 * The site is a separate route rather than something embedded, so the
 * learner can keep the lesson open beside it - which is how the task is
 * meant to be worked through.
 *
 * The practical is not optional. The lesson will not move past this
 * page until the lab says it has been finished, so this page has to be
 * plain about that before the button is pressed rather than leaving a
 * child staring at a Next button that refuses to work.
 *
 * `onComplete` still fires on the click. It is the fallback gate for a
 * browser that will not keep the finished flag at all - see
 * lib/practical.js - where "opened it" is the most the lesson can ask
 * for without trapping anybody.
 */
export function LabPage({ page, onComplete, done = false, required = true, gated = true }) {
  return (
    <div className="page-lab">
      <h2>{page.title}</h2>
      {page.body?.map((p, i) => <p key={i}>{p}</p>)}

      {page.analogy && (
        <>
          <div className="analogy-grid">
            {page.analogy.parts.map((part) => (
              <div key={part.component} className={`analogy-card t-${part.color ?? 'grape'}`}>
                <span className="analogy-icon" aria-hidden="true">{part.icon}</span>
                <strong>{part.component}</strong>
                <span className="analogy-body">{part.body}</span>
                <p>{part.text}</p>
              </div>
            ))}
          </div>
          {page.analogy.caption && <p className="analogy-caption">{page.analogy.caption}</p>}
        </>
      )}

      {page.site && (
        <>
          <div className="lab-launch">
            <div>
              <strong>{page.site.title ?? 'Practical task'}</strong>
              <p className="small muted">{page.site.note}</p>
            </div>
            <a
              href={page.site.href}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-site"
              onClick={() => onComplete?.()}
            >
              🗔 {done ? 'Open it again' : page.site.label ?? 'Practical'}
            </a>
          </div>

          {(required || done) && (
          <p className={`lab-required ${done ? 'is-done' : ''}`}>
            {done ? (
              <>
                <span aria-hidden="true">✓</span> Practical finished — you can carry on.
              </>
            ) : gated ? (
              <>
                <span aria-hidden="true">🔒</span> This one has to be done. Work through to the
                last screen and the Next button here unlocks on its own — you can leave the
                practical open in its tab.
              </>
            ) : (
              <>
                <span aria-hidden="true">🔒</span> This one has to be done. Open it and work
                through to the last screen before you carry on.
              </>
            )}
          </p>
          )}
        </>
      )}
    </div>
  );
}
